import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import css from "./DeliveryDetailsPopup.module.css";
import { ImageReusable } from "../../../ui";

const DeliveryDetailsPopup = ({ delivery, onClose }) => {
  const { t, i18n } = useTranslation();
  const [tooltip, setTooltip] = useState({ show: false, text: "", x: 0, y: 0 });
  const currentLanguage = i18n.language;

  if (!delivery) return null;

  const getLocalizedValue = (value) => {
    if (typeof value === "object" && value !== null) {
      return value[currentLanguage] || value.en || value.ru || value.ge || "";
    }
    return value || "";
  };

  const truncateText = (text, maxLength = 25) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const handleMouseEnter = (e, text) => {
    if (text && text.length > 25) {
      const rect = e.target.getBoundingClientRect();
      setTooltip({
        show: true,
        text,
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: "", x: 0, y: 0 });
  };

  return (
    <div className={css.popupOverlay} onClick={onClose}>
      <div className={css.popupContent} onClick={(e) => e.stopPropagation()}>
        <div className={css.popupHeader}>
          <h2>{t("order-info")}</h2>
          <button className={css.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={css.popupBody}>
          <div className={css.clientInfo}>
            <div className={css.infoSection}>
              <h3>{t("Client Information")}</h3>
              <div className={css.infoGrid}>
                <div className={css.infoRow}>
                  <span className={css.label}>
                    {t("modal-name-input-new-category")}:
                  </span>
                  <span className={css.value}>
                    {getLocalizedValue(delivery.clientName)}
                  </span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("contact")}:</span>
                  <span className={css.value}>{delivery.contact}</span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("address")}:</span>
                  <span className={css.value}>
                    {delivery.deliveryStreet} {delivery.buidlingNumber}
                  </span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("Entrance")}:</span>
                  <span className={css.value}>
                    {delivery.entranceNumber || t("Not specified")}
                  </span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("Floor")}:</span>
                  <span className={css.value}>
                    {delivery.floorNumber || t("Not specified")}
                  </span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("Apartment")}:</span>
                  <span className={css.value}>
                    {delivery.apartmnetNumber || t("Not specified")}
                  </span>
                </div>
              </div>
            </div>

            <div className={css.infoSection}>
              <h3>{t("order-info")}</h3>
              <div className={css.infoGrid}>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("payment-number")}:</span>
                  <span className={css.value}>#{delivery.id}</span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("diet-type")}:</span>
                  <span className={css.value}>
                    {getLocalizedValue(delivery.dietName)}
                  </span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("debt")}:</span>
                  <span
                    className={`${css.value} ${delivery.debt > 0 ? css.debtPositive : css.debtZero}`}
                  >
                    {delivery.debt || "0"}
                  </span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("delivery-interval")}:</span>
                  <span className={css.value}>
                    {delivery.deliveryInterval || t("Not specified")}
                  </span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("courier")}:</span>
                  <span className={css.value}>
                    {delivery.assignedCourier || t("Not assigned")}
                  </span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("comment")}:</span>
                  <span
                    className={css.value}
                    onMouseEnter={(e) =>
                      handleMouseEnter(e, delivery.commentForDelivery)
                    }
                    onMouseLeave={handleMouseLeave}
                  >
                    {truncateText(delivery.commentForDelivery) || t("no-data")}
                  </span>
                </div>
                <div className={css.infoRow}>
                  <span className={css.label}>{t("location-on-map")}:</span>
                  <span className={css.value}>
                    {delivery.latitude && delivery.longitude
                      ? `${delivery.latitude}, ${delivery.longitude}`
                      : t("Not specified")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {delivery.dishes && delivery.dishes.length > 0 && (
            <div className={css.dishesSection}>
              <h3>{t("orders-menu-title-sample-dishes")}</h3>
              <div className={css.dishesGrid}>
                {delivery.dishes.map((dish, index) => (
                  <React.Fragment key={index}>
                    <div className={css.dishCard}>
                      <div className={css.dishImageContainer}>
                        <ImageReusable
                          src={dish.img}
                          className={css.dishImage}
                          alt={t("Image")}
                        />
                      </div>
                      <div className={css.dishInfo}>
                        <h4>{getLocalizedValue(dish.name)}</h4>
                        <div className={css.dishDetails}>
                          <div className={css.dishSpecs}>
                            {dish.weightNetto && (
                              <span className={css.specItem}>
                                {dish.weightNetto} {t("weight-netto")}
                              </span>
                            )}
                            {dish.energy && (
                              <span className={css.specItem}>
                                {dish.energy} {t("modal-energy")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {dish.proteinDish && (
                      <div className={`${css.dishCard} ${css.proteinDishCard}`}>
                        <div className={css.dishImageContainer}>
                          <ImageReusable
                            src={dish.proteinDish.img}
                            className={css.dishImage}
                            alt={t("Image")}
                          />
                        </div>
                        <div className={css.dishInfo}>
                          <h4>{getLocalizedValue(dish.proteinDish.name)} ({t("Protein Dish")})</h4>{" "}
                          <div className={css.dishDetails}>
                            <div className={css.dishSpecs}>
                              {dish.proteinDish.weightNetto && (
                                <span className={css.specItem}>
                                  {dish.proteinDish.weightNetto}{" "}
                                  {t("weight-netto")}
                                </span>
                              )}
                              {dish.proteinDish.energy && (
                                <span className={css.specItem}>
                                  {dish.proteinDish.energy} {t("modal-energy")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {dish.sauceDish && (
                      <div className={`${css.dishCard} ${css.sauceDishCard}`}>
                        <div className={css.dishImageContainer}>
                          <ImageReusable
                            src={dish.sauceDish.img}
                            className={css.dishImage}
                            alt={t("Image")}
                          />
                        </div>
                        <div className={css.dishInfo}>
                          <h4>{getLocalizedValue(dish.sauceDish.name)} ({t("Sauce Dish")})</h4>
                          <div className={css.dishDetails}>
                            <div className={css.dishSpecs}>
                              {dish.sauceDish.weightNetto && (
                                <span className={css.specItem}>
                                  {dish.sauceDish.weightNetto}{" "}
                                  {t("weight-netto")}
                                </span>
                              )}
                              {dish.sauceDish.energy && (
                                <span className={css.specItem}>
                                  {dish.sauceDish.energy} {t("modal-energy")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {tooltip.show && (
        <div
          className={css.tooltip}
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default DeliveryDetailsPopup;
